/**
 * RebalanceBadge — the in-app half of the rebalance, now the whole of it
 * (WORK.md item 45: the rebalance lives in the app, not in an email).
 *
 * A dot on the Projections nav entry when the active portfolio's LATEST SAVED
 * run of the rule that GOVERNS it (D20/D21, `rebalanceRuns.ts`) said to act:
 * with a target allocation that is the drift check (`rebalance` — the book
 * drifted far enough on return or risk), without one today's optimiser rule
 * (`act` — it cleared both of its halves). An optimiser "hold" run on a book
 * with a target says nothing about drift, so it never clears a drift dot.
 * A portfolio that has chosen a model (item 57) is governed by the model
 * check instead (`rebalance` — a new book beats today's, net, by the bar);
 * one with no model reads exactly as before.
 * Nothing else badges — see `isActionable`.
 *
 * The nightly record decides (item 57 step 6; the owner's word of 2026-11-01,
 * "Nightly record decides"): once the nightly model batch has a row for a
 * portfolio the model rule governs, the row's verdict is the dot's, and an
 * on-demand model run shows only in the Model tab. Its `could_not_run` and
 * `no_model` draw nothing, as every verdict but `act` and `rebalance` does.
 * With no row, or no read, the dot reads the on-demand runs as before.
 *
 * No expiry. The old badge read a per-device verdict that vanished after 24 h;
 * this one reads the saved history, so it says the same thing on every device
 * and the label carries the run's DATE — an old verdict is shown as old rather
 * than hidden. A nightly row's label carries the date it was recorded, and
 * says when the nightly check is overdue.
 *
 * One component rather than markup in each shell, because the desktop sub-row
 * and the mobile drawer both render the same nav entry and a badge that
 * appeared in one but not the other would be a bug nobody notices for months.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useActivePortfolio } from '@/lib/activePortfolio';
import { nightlyBadgeLabel } from '@/lib/modelState';
import {
  getBadgeRow,
  getBadgeRun,
  isActionable,
  loadLatestRun,
  loadModelState,
  subscribeRebalanceRuns,
  type RebalanceRun,
} from '@/lib/rebalanceRuns';
import { cn } from '@/lib/utils';

function pp(x: number | null | undefined, signed = false): string {
  if (x == null || !Number.isFinite(x)) return '—';
  return `${signed && x >= 0 ? '+' : ''}${(x * 100).toFixed(2)}pp`;
}

/**
 * The label carries the NUMBER and the half, not just "action needed": a
 * badge that says only "look at this" makes the owner open the panel to find
 * out whether it was worth opening.
 */
export function rebalanceBadgeLabel(run: RebalanceRun): string {
  const head = `Rebalance suggested (${run.date})`;
  if (run.rule === 'model') {
    return `${head}: the model's book, ${pp(run.net, true)} net return a year over today's`;
  }
  if (run.rule === 'drift') {
    const ret = `${pp(run.net, true)} net return gap`;
    const risk = `tracking error ${pp(run.trackingError)}`;
    if (run.trigger === 'both') return `${head}: on return and risk — ${ret}, ${risk}`;
    if (run.trigger === 'risk') return `${head}: on risk — ${risk}`;
    return `${head}: on return — ${ret}`;
  }
  return `${head}: ${pp(run.net)} net uplift`
    + (run.probability != null ? `, ${(run.probability * 100).toFixed(0)}% confidence` : '');
}

export function RebalanceBadge({ className }: { className?: string }) {
  const { activePortfolioId } = useActivePortfolio();
  const row = useSyncExternalStore(
    subscribeRebalanceRuns,
    () => (activePortfolioId ? getBadgeRow(activePortfolioId) : null),
    () => null,
  );
  const run = useSyncExternalStore(
    subscribeRebalanceRuns,
    () => (activePortfolioId ? getBadgeRun(activePortfolioId) : null),
    () => null,
  );

  // Load once per portfolio (the runs, and whether it has a target), and the
  // nightly rows once per app load; the cache notifies when each lands. A
  // read of the runs that fails leaves them unloaded and the badge simply
  // absent; a failed nightly read leaves the dot on the runs.
  useEffect(() => {
    if (!activePortfolioId) return;
    if (getBadgeRun(activePortfolioId) === undefined) void loadLatestRun(activePortfolioId);
    void loadModelState();
  }, [activePortfolioId]);

  // A model portfolio waits for the nightly read, which may overrule its runs.
  if (!activePortfolioId || row === undefined) return null;
  const label = row
    ? (isActionable(row) ? nightlyBadgeLabel(row, Date.now()) : null)
    : (run && isActionable(run) ? rebalanceBadgeLabel(run) : null);
  if (!label) return null;
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      className={cn(
        'ml-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary align-middle',
        className,
      )}
    />
  );
}
